import React from 'react';
import { Card } from '@/components/ui/card';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Chart } from '../engine/chart';
import { PLANETS } from '../data/constants';

interface Props {
  chart: Chart;
}

/**
 * Lower-triangular aspect matrix. Rows/cols are planets in chart order.
 * Each cell shows the aspect glyph (in its color) if one exists, else blank.
 */
export const AspectGrid: React.FC<Props> = ({ chart }) => {
  const names = Object.keys(chart.planets);

  // Build a quick lookup keyed by unordered pair.
  const lookup = new Map<string, typeof chart.aspects[number]>();
  for (const a of chart.aspects) {
    const key = [a.p1, a.p2].sort().join('|');
    lookup.set(key, a);
  }

  const glyphFor = (n: string) => PLANETS.find((p) => p.name === n)?.glyph ?? n[0];

  return (
    <Card className="p-5 bg-card/50 overflow-x-auto">
      <TooltipProvider delayDuration={150}>
        <table className="border-collapse text-center mx-auto">
          <tbody>
            {names.map((rowName, i) => (
              <tr key={rowName}>
                {names.slice(0, i).map((colName) => {
                  const key = [rowName, colName].sort().join('|');
                  const asp = lookup.get(key);
                  return (
                    <td
                      key={colName}
                      className="w-8 h-8 border border-border/40 align-middle"
                    >
                      {asp ? (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span
                              className="inline-block text-base font-serif cursor-help leading-none"
                              style={{ color: asp.color }}
                            >
                              {asp.symbol}
                            </span>
                          </TooltipTrigger>
                          <TooltipContent>
                            <div className="text-xs">
                              <div className="font-medium">
                                {glyphFor(asp.p1)} {asp.symbol} {glyphFor(asp.p2)} — {asp.name}
                              </div>
                              <div className="text-muted-foreground">
                                orb {asp.orb_actual.toFixed(2)}°
                              </div>
                            </div>
                          </TooltipContent>
                        </Tooltip>
                      ) : null}
                    </td>
                  );
                })}
                {/* Diagonal label cell with the row planet glyph */}
                <td className="w-8 h-8 border border-border/60 bg-muted/30 align-middle">
                  <span
                    className="inline-block text-base font-serif leading-none"
                    style={{ color: PLANETS.find((p) => p.name === rowName)?.color }}
                    title={rowName}
                  >
                    {glyphFor(rowName)}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </TooltipProvider>
      <p className="mt-3 text-xs text-muted-foreground text-center">
        Hover any glyph to see the aspect name and exact orb.
      </p>
    </Card>
  );
};